import { canManageOrganization } from 'util/organizationAccess';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Col,
  Form,
  InputGroup,
  Offcanvas,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import PageHeader from 'components/common/PageHeader';
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import Pagination from 'components/common/Pagination';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLocationArrow,
  faPlusCircle
} from '@fortawesome/free-solid-svg-icons';
import corner6 from 'assets/img/illustrations/corner-6.png';
import _ from 'lodash';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import SimpleBarReact from 'simplebar-react';

import locationHelper from 'helpers/location-helper';
import ActionButton from 'components/common/ActionButton';
import { showAlert, showConfirm } from 'stores/modal';
import LocationForm from './components/LocationForm';
import {
  useAddTenantLocationMutation,
  useListTenantLocationsQuery,
  useRemoveTenantLocationMutation,
  useUpdateTenantLocationMutation
} from 'rtk/org-admin/location.slice';

const DEFAULT_PAGE_SIZE = 10;

function formatLocationRow(e) {
  if (!e) return '—';
  const parts = [
    e.Address1,
    e.Address2,
    e.City,
    e.State ? locationHelper.getStateNameByCode(e.State) : null,
    e.PostalCode
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
}

function getLocationDescription(e) {
  const value = e?.Description || '';
  const text = typeof value === 'string' ? value.trim() : '';
  return text.length ? text : '';
}

const AdminLocation = () => {
  const canManage = useSelector(state =>
    canManageOrganization(state.authentication?.userRoles)
  );
  const dispatch = useDispatch();
  const userMaster = useSelector(state => state.app.userMaster);
  const tenantGuid = userMaster?.TenantGUID || null;

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const indexStart = pageIndex * pageSize;

  const {
    data: locationsRes,
    isFetching,
    isLoading,
    isError: locationsLoadError,
    refetch
  } = useListTenantLocationsQuery(
    {
      tenantGuid,
      indexStart,
      maxResults: pageSize,
      search: searchTerm
    },
    { skip: !tenantGuid }
  );

  const [addTenantLocation] = useAddTenantLocationMutation();
  const [updateTenantLocation] = useUpdateTenantLocationMutation();
  const [removeTenantLocation] = useRemoveTenantLocationMutation();

  const items = locationsRes?.Objects || [];
  const totalRecords = locationsRes?.TotalRecords || 0;
  const endOfResults = !!locationsRes?.EndOfResults;

  const loading = isFetching || isLoading;

  const routerLocation = useLocation();
  const navigate = useNavigate();
  const [preAddress, setPreAddress] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [formTitle, setFormTitle] = useState('Add new location');
  const [savingLocation, setSavingLocation] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm();

  // react-hook-form keeps the last values of registered fields and writes them back into the
  // inputs when the panel reopens, so reset to the location being edited (or to blank for add).
  const resetFormValues = location =>
    reset({
      location_name: location?.DisplayName || '',
      address_1: location?.Address1 || '',
      address_2: location?.Address2 || '',
      city: location?.City || '',
      zip: location?.PostalCode || ''
    });

  const debouncedSetSearch = useMemo(() => {
    return _.debounce(value => {
      setSearchTerm(value);
      setPageIndex(0);
    }, 250);
  }, []);

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  useEffect(() => {
    setPageIndex(0);
  }, [pageSize]);

  const addNewLocation = () => {
    resetFormValues(null);
    setPreAddress(null);
    setFormTitle('Add new location');
    setShowAddressForm(true);
  };

  // Arriving from "+ Add new location" in another dialog opens the Add panel straight away.
  useEffect(() => {
    if (routerLocation.state?.openAddLocation) {
      addNewLocation();
      navigate(routerLocation.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLocation.state]);

  const editLocation = location => {
    resetFormValues(location);
    setPreAddress(location);
    setFormTitle('Update location');
    setShowAddressForm(true);
  };

  const deleteLocation = location => {
    const guid = location?.GUID;
    if (!guid) {
      dispatch(showAlert('Unable to delete', 'Missing location identifier.'));
      return;
    }
    dispatch(
      showConfirm(
        'Are you sure?',
        'Are you sure you want to remove this location?',
        confirmed => {
          if (!confirmed) return;

          removeTenantLocation({ tenantGuid, guid })
            .unwrap()
            .then(() => {
              toast.success('Location removed successfully', {
                theme: 'colored',
                position: 'bottom-center'
              });
              setPageIndex(0);
            })
            .catch(err => {
              dispatch(
                showAlert(
                  'Error deleting location',
                  err?.Message ||
                    'There was a problem removing this location. Please try again.'
                )
              );
            });
        }
      )
    );
  };

  const saveLocation = location => {
    setSavingLocation(true);
    const isUpdate = !!preAddress?.GUID;
    const payload = {
      tenantGuid,
      displayName: location.location_name,
      address_1: location.address_1,
      address_2: location.address_2,
      city: location.city,
      state: location.state,
      postal: location.zip,
      description: location.description
    };

    const call = isUpdate
      ? updateTenantLocation({ ...payload, guid: preAddress.GUID })
      : addTenantLocation(payload);

    call
      .unwrap()
      .then(() => {
        toast.success(isUpdate ? 'Location updated.' : 'Location created.', {
          theme: 'colored',
          position: 'bottom-center'
        });
        setShowAddressForm(false);
        setPageIndex(0);
      })
      .catch(err => {
        dispatch(
          showAlert(
            'Unable to save location',
            err?.Message || 'Failed to save location. Please try again.'
          )
        );
      })
      .finally(() => setSavingLocation(false));
  };

  const onSearchChange = e => {
    const value = e.target.value || '';
    setSearchInput(value);
    debouncedSetSearch(value);
  };

  return (
    <>
      {/* <PageHeader
        title="Locations"
        description="Manage saved locations."
        className="mb-3"
        titleTag="h2"
        image={corner6}
      /> */}

      <Row className="mb-3 g-3">
        <Col lg={12}>
          <FalconComponentCard noGuttersBottom>
            <FalconComponentCard.Header
              image={corner6}
              className="px-4"
              title="Locations"
              subtitle="Manage saved locations"
              endEl={
                <Flex className="g-2">
                  <div className="mx-1">
                    <InputGroup>
                      <Form.Control
                        type="search"
                        value={searchInput}
                        onChange={onSearchChange}
                        placeholder="Search locations..."
                        name="search"
                      />
                      <InputGroup.Text className="bg-white">
                        <FontAwesomeIcon icon="search" />
                      </InputGroup.Text>
                    </InputGroup>
                  </div>
                  {canManage && (
                    <div className="mx-1">
                      <Button variant="primary" onClick={addNewLocation}>
                        <FontAwesomeIcon icon={faPlusCircle} size="sm" /> Add
                        New
                      </Button>
                    </div>
                  )}
                </Flex>
              }
            />

            <FalconComponentCard.Body noLight className="py-0 px-4">
              {isLoading && (
                <Flex justifyContent="center" className="py-3">
                  <Spinner animation="border" size="sm" />
                </Flex>
              )}

              {items.length > 0 && !isLoading && (
                <Pagination
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  totalRecords={totalRecords}
                  currentCount={items.length}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  endOfResults={endOfResults}
                  disabled={loading}
                  pageSizeOptions={[5, 10, 15, 25]}
                  onRefresh={() => refetch()}
                  loading={loading}
                />
              )}

              {items.length > 0 && !locationsLoadError && !isLoading && (
                <SimpleBarReact>
                  <Table
                    striped
                    bordered
                    className={`fs--1 mb-0${
                      loading ? ' refreshing-skeleton' : ''
                    }`}
                  >
                    <thead className="bg-200 text-900 text-nowrap align-middle">
                      <tr>
                        <th scope="col">Name</th>
                        <th scope="col">Location</th>
                        <th scope="col">Created</th>
                        <th className="text-end" scope="col">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(e => {
                        const createdDate = e?.Created
                          ? moment.utc(e.Created).local().format('l, LT')
                          : '—';
                        const desc = getLocationDescription(e);
                        return (
                          <tr
                            key={e?.GUID || e?.Id || e?.id || createdDate}
                            className="align-middle white-space-nowrap"
                          >
                            <td className="align-middle py-2">
                              <div className="fw-semi-bold">
                                {e?.DisplayName || '—'}
                              </div>
                              {desc ? (
                                <div className="text-600 fs--2">{desc}</div>
                              ) : null}
                            </td>
                            <td className="align-middle py-2">
                              {formatLocationRow(e)}
                            </td>
                            <td className="align-middle py-2">{createdDate}</td>
                            <td className="align-middle py-2 text-end">
                              {canManage && (
                                <>
                                  <ActionButton
                                    icon="edit"
                                    title="Edit"
                                    label="Edit"
                                    variant="action"
                                    className="p-0 me-3 text-primary"
                                    onClick={() => editLocation(e)}
                                  />
                                  <ActionButton
                                    icon="trash-alt"
                                    title="Remove"
                                    label="Remove"
                                    variant="action"
                                    className="p-0 text-danger"
                                    onClick={() => deleteLocation(e)}
                                  />
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </SimpleBarReact>
              )}

              {items.length === 0 &&
                !loading &&
                (locationsLoadError ? (
                  <div className="text-center py-5">
                    <FontAwesomeIcon icon={faLocationArrow} size="3x" />
                    <p className="my-3 text-600 fs--1">
                      Unable to load locations. Try again.
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <FontAwesomeIcon icon={faLocationArrow} size="3x" />
                    <p className="my-3 text-600 fs--1">
                      No locations found.
                      {searchTerm
                        ? ' Try adjusting your search.'
                        : canManage
                        ? ' Click "Add New" to create a location.'
                        : ''}
                    </p>
                  </div>
                ))}
            </FalconComponentCard.Body>
          </FalconComponentCard>
        </Col>
      </Row>

      <Offcanvas
        id="offcanvas-admin-location-form"
        show={showAddressForm}
        backdrop="static"
        onHide={() => !savingLocation && setShowAddressForm(false)}
        placement="end"
      >
        <Offcanvas.Header closeButton={!savingLocation}>
          <Offcanvas.Title>{formTitle}</Offcanvas.Title>
        </Offcanvas.Header>
        <hr className="my-0" />
        <Offcanvas.Body>
          <Form onSubmit={handleSubmit(saveLocation)}>
            <LocationForm
              preAddress={preAddress}
              setAddressValue={saveLocation}
              setShowAddressForm={setShowAddressForm}
              savingLocation={savingLocation}
              register={register}
              errors={errors}
              col={12}
            />
          </Form>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default AdminLocation;
